import { Status } from "../../../models/gameplan";
import {
  type CourseSlice,
  type LearningPathSlice,
  type LessonSlice,
  type ModuleSlice,
  type SlideSlice,
  type WorkoutSlice,
} from "./data.types";

export enum ActionTypes {
  "DATA_SET_LEARNING_PATH" = "DATA_SET_LEARNING_PATH",
  "DATA_SET_COURSES" = "DATA_SET_COURSES",
  "DATA_SET_COURSE" = "DATA_SET_COURSE",
  "DATA_SET_COURSE_STATUS_BY_ID" = "DATA_SET_COURSE_STATUS_BY_ID",
  "DATA_SET_COURSE_COMMITMENT_STATUS_BY_ID" = "DATA_SET_COURSE_COMMITMENT_STATUS_BY_ID",
  "DATA_SET_MODULES" = "DATA_SET_MODULES",
  "DATA_SET_MODULE" = "DATA_SET_MODULE",
  "DATA_SET_MODULE_STATUS_BY_ID" = "DATA_SET_MODULE_STATUS_BY_ID",
  "DATA_SET_LESSONS" = "DATA_SET_LESSONS",
  "DATA_SET_LESSON" = "DATA_SET_LESSON",
  "DATA_SET_LESSON_STATUS_BY_ID" = "DATA_SET_LESSON_STATUS_BY_ID",
  "DATA_SET_WORKOUTS" = "DATA_SET_WORKOUTS",
  "DATA_SET_WORKOUT" = "DATA_SET_WORKOUT",
  "DATA_SET_WORKOUT_STATUS_BY_ID" = "DATA_SET_WORKOUT_STATUS_BY_ID",
  "DATA_SET_SLIDES" = "DATA_SET_SLIDES",
}

export const setLearningPath = (learningPath: LearningPathSlice) => ({
  type: ActionTypes.DATA_SET_LEARNING_PATH as const,
  payload: {
    learningPath,
  },
});

export const setCourses = (courses: CourseSlice[]) => ({
  type: ActionTypes.DATA_SET_COURSES as const,
  payload: {
    courses,
  },
});

export const setCourse = (course: CourseSlice) => ({
  type: ActionTypes.DATA_SET_COURSE as const,
  payload: {
    course,
  },
});

export const setCourseStatusById = (
  courseId: CourseSlice["id"],
  status: Status
) => ({
  type: ActionTypes.DATA_SET_COURSE_STATUS_BY_ID as const,
  payload: {
    courseId,
    status,
  },
});

export const setCourseCommitmentStatusById = (
  courseId: CourseSlice["id"],
  status: Status
) => ({
  type: ActionTypes.DATA_SET_COURSE_COMMITMENT_STATUS_BY_ID as const,
  payload: {
    courseId,
    status,
  },
});

export const setModules = (modules: ModuleSlice[]) => ({
  type: ActionTypes.DATA_SET_MODULES as const,
  payload: {
    modules,
  },
});

export const setModule = (module: ModuleSlice) => ({
  type: ActionTypes.DATA_SET_MODULE as const,
  payload: {
    module,
  },
});

export const setModuleStatusById = (
  moduleId: ModuleSlice["id"],
  status: Status
) => ({
  type: ActionTypes.DATA_SET_MODULE_STATUS_BY_ID as const,
  payload: {
    moduleId,
    status,
  },
});

export const setLessons = (lessons: LessonSlice[]) => ({
  type: ActionTypes.DATA_SET_LESSONS as const,
  payload: {
    lessons,
  },
});

export const setLesson = (lesson: LessonSlice) => ({
  type: ActionTypes.DATA_SET_LESSON as const,
  payload: {
    lesson,
  },
});

export const setLessonStatusById = (
  lessonId: LessonSlice["id"],
  status: Status
) => ({
  type: ActionTypes.DATA_SET_LESSON_STATUS_BY_ID as const,
  payload: {
    lessonId,
    status,
  },
});

export const setWorkouts = (workouts: WorkoutSlice[]) => ({
  type: ActionTypes.DATA_SET_WORKOUTS as const,
  payload: {
    workouts,
  },
});

export const setWorkout = (workout: WorkoutSlice) => ({
  type: ActionTypes.DATA_SET_WORKOUT as const,
  payload: {
    workout,
  },
});

export const setWorkoutStatusById = (
  workoutId: WorkoutSlice["id"],
  status: Status
) => ({
  type: ActionTypes.DATA_SET_WORKOUT_STATUS_BY_ID as const,
  payload: {
    workoutId,
    status,
  },
});

export const setSlides = (slides: SlideSlice[]) => ({
  type: ActionTypes.DATA_SET_SLIDES as const,
  payload: {
    slides,
  },
});

const Actions = {
  setLearningPath,
  setCourses,
  setCourse,
  setCourseStatusById,
  setCourseCommitmentStatusById,
  setModules,
  setModule,
  setModuleStatusById,
  setLessons,
  setLesson,
  setLessonStatusById,
  setWorkouts,
  setWorkout,
  setWorkoutStatusById,
  setSlides,
};

export type GenericAction = {
  type: string;
  payload: {
    learningPath?: LearningPathSlice;
    courses?: CourseSlice[];
    course?: CourseSlice;
    courseId?: CourseSlice["id"];
    status?: Status;
    modules?: ModuleSlice[];
    module?: ModuleSlice;
    moduleId?: ModuleSlice["id"];
    lessons?: LessonSlice[];
    lesson?: LessonSlice;
    lessonId?: LessonSlice["id"];
    workouts?: WorkoutSlice[];
    workout?: WorkoutSlice;
    workoutId?: WorkoutSlice["id"];
    slides?: SlideSlice[];
  };
};

export type Actions = typeof Actions;
export type ActionKeys = keyof Actions;
export type Action = Actions[ActionKeys];
export type ActionReturns = ReturnType<Action> | GenericAction;
